import { useTranslation } from "react-i18next";
import Cards from "src/components/Cards/Cards";
import { MdContentCopy } from "react-icons/md";
import { showNotification } from "src/helpers/utils";
import { IButtonWithDropdown } from "src/components/ButtonWithDropdown/ButtonWithDropdown";
import { usePagination } from "src/hooks/usePagination";
import useMobile from "src/hooks/useMobile";
import CardsPagination from "src/components/Cards/CardsPagination";
import clsx from "clsx";
import { useMemo } from "react";
import { EButtonKind, IButton } from "src/components/Button/Button.types";

interface IContactCards {
  data: any[];
  showCopy?: boolean;
  columns?: number;
  actions?: Array<IButton | IButtonWithDropdown>;
  customPageSize?: number | null;
}

const hasOptions = (
  button: IButton | IButtonWithDropdown,
): button is IButtonWithDropdown => {
  return Array.isArray((button as IButtonWithDropdown)?.options);
};

const ContactCards: React.FC<IContactCards> = ({
  data,
  showCopy = false,
  columns = 1,
  actions = [],
  customPageSize = null,
}) => {
  const { isMobile } = useMobile();
  const { t } = useTranslation();

  const pageSize = useMemo(() => {
    return customPageSize !== null ? customPageSize : isMobile ? 3 : 4;
  }, [isMobile]);

  const {
    items,
    canNext,
    next,
    prev,
    canPrev,
    totalPages,
    page,
    goTo,
    showPagination,
  } = usePagination(data, {
    pageSize,
  });

  if (!data?.length) return null;

  return (
    <div className={clsx(["flex", "w-full", "gap-4", "flex-col"])}>
      <Cards columns={columns} mobileColumns={1}>
        {items?.map?.((data: any, key: number) => {
          return (
            <Cards.ContactInfo
              key={key}
              value={data?.value}
              icon={data?.icon}
              name={data?.name}
              type={data?.type}
              notes={data?.notes}
              buttons={[
                {
                  icon: <MdContentCopy />,
                  kind: EButtonKind.NORMAL,
                  onClick: async (e: any) => {
                    e.stopPropagation();
                    e.preventDefault();
                    try {
                      await navigator.clipboard.writeText(data?.value);
                      showNotification({
                        message: t("misc:strings.copied_to_clipboard"),
                        kind: "success",
                      });
                    } catch (err) {
                      showNotification({
                        message: t("Error copiando"),
                        kind: "error",
                      });
                    }
                  },
                  hide: !showCopy,
                  tooltip: t("Copiar"),
                },
                ...(actions?.length
                  ? actions.map((action: IButton | IButtonWithDropdown) => {
                      if (hasOptions(action)) {
                        return {
                          ...action,
                          onClick: (e: any) => {
                            e.stopPropagation();
                            e.preventDefault();

                            action?.onClick?.(key);
                          },
                          options: action.options.map((optionAction) => {
                            return {
                              ...optionAction,
                              onClick: (e: any) => {
                                e.stopPropagation();
                                e.preventDefault();

                                optionAction?.onClick?.(key);
                              },
                            };
                          }),
                        };
                      }

                      return {
                        ...action,
                        onClick: (e: any) => {
                          e.stopPropagation();
                          e.preventDefault();

                          action?.onClick?.(key);
                        },
                      };
                    })
                  : []),
              ]}
            />
          );
        })}
      </Cards>
      {showPagination && (
        <CardsPagination
          canNext={canNext}
          canPrev={canPrev}
          goTo={goTo}
          next={next}
          prev={prev}
          page={page}
          totalPages={totalPages}
          totalItems={data?.length}
          pageSize={pageSize}
        />
      )}
    </div>
  );
};

export default ContactCards;
